import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState, AppDispatch } from '../app/redux/store';
import { fetchEnhancedUserInfo, fetchMySubAccounts } from '../app/redux/slices/masterSubAccountSlice';
import { selectIsAuthenticated } from '../app/redux/slices/authSlice';
import Navigation from '../app/components/NavigationSPA';
import CreateSubAccountModal from '../app/components/CreateSubAccountModal';
import { useTranslation } from 'react-i18next';
import { logger } from '../app/utils/logger';

// Team Card Component
interface TeamCardProps {
  team: {
    id: string;
    name: string;
    displayName: string;
    company: string;
    type: 'master' | 'sub';
    isActive: boolean;
    probesCount: number;
    createdAt?: string;
  };
  onClick: () => void;
}

const TeamCard = ({ team, onClick }: TeamCardProps) => {
  const { t } = useTranslation(['teams']);
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-xl hover:border-blue-300 hover:scale-[1.02] transition-all duration-200 border-2 border-transparent"
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <h3 className="text-lg font-semibold text-gray-900">{team.company}</h3>
            {team.type === 'master' && (
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                {t('master')}
              </span>
            )}
            {!team.isActive && (
              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded">
                {t('inactive')}
              </span>
            )}
          </div>
          <div className="space-y-1">
            <p className="text-sm text-gray-900">
              <span className="font-medium text-gray-900">{t('company')}:</span> {team.company}
            </p>
            <p className="text-sm text-gray-900">
              <span className="font-medium text-gray-900">{t('accountName')}:</span> {team.displayName}
            </p>
          </div>
        </div>
        
        <div className="ml-4">
          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

// Loading Component
const LoadingState = () => {
  const { t } = useTranslation(['teams']);
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
      <p className="text-gray-900">{t('loadingTeams')}</p>
    </div>
  );
};

// Empty State Component
const EmptyState = ({ onCreateClick }: { onCreateClick: () => void }) => {
  const { t } = useTranslation(['teams']);
  return (
    <div className="bg-white rounded-lg shadow-md p-12 text-center">
      <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('noSubAccountsYet')}</h3>
      <p className="text-gray-900 mb-6">{t('createFirstSubAccount')}</p>
      <button
        onClick={onCreateClick}
        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        {t('createSubAccountButton')}
      </button>
    </div>
  );
};

// Error State Component
const ErrorState = ({ message, onRetry }: { message: string; onRetry: () => void }) => {
  const { t } = useTranslation(['teams']);
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
      <div className="flex items-center gap-3 text-red-800 mb-4">
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" />
        </svg>
        <span className="font-semibold">{t('errorLoadingTeams')}</span>
    </div>
    <p className="text-red-700 mb-4">{message}</p>
    <button
      onClick={onRetry}
      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
    >
      {t('tryAgain')}
    </button>
  </div>
  );
};

// Access Denied Component
const AccessDenied = () => {
  const { t } = useTranslation(['teams']);
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md text-center">
        <svg className="w-16 h-16 mx-auto text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('accessDenied')}</h2>
        <p className="text-gray-900 mb-6">{t('onlyMasterAccounts')}</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-block px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {t('goToDashboard')}
        </button>
      </div>
    </div>
  );
};

// Main Teams Page Component
export default function TeamsPage() {
  const { t } = useTranslation(['teams']);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { 
    isMasterAccount, 
    allTeamsData, 
    masterAccountInfo,
    loading,
    errors 
  } = useSelector((state: RootState) => state.masterSubAccount);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Debug logging for Redux state
  useEffect(() => {
    logger.log('🔍 === TEAMS PAGE REDUX STATE ===');
    logger.log('👑 isMasterAccount:', isMasterAccount);
    logger.log('📁 masterAccountInfo:', masterAccountInfo);
    logger.log('📊 allTeamsData:', allTeamsData);
    logger.log('⏳ loading.userInfo:', loading.userInfo);
    logger.log('⏳ loading.subAccounts:', loading.subAccounts);
    logger.log('❌ errors.userInfo:', errors.userInfo);
    logger.log('❌ errors.subAccounts:', errors.subAccounts);
    logger.log('🔍 === END TEAMS PAGE STATE ===');
  }, [isMasterAccount, masterAccountInfo, allTeamsData, loading, errors]);

  // Handle hydration
  useEffect(() => {
    setMounted(true);
  }, []);

  // Check authentication and redirect if needed
  useEffect(() => {
    if (mounted && !isAuthenticated) {
      navigate('/login');
    }
  }, [mounted, isAuthenticated, navigate]);

  // Fetch user info and teams on mount and when navigating to this page
  useEffect(() => {
    if (mounted && isAuthenticated && isMasterAccount) {
      logger.log('🔄 Teams page: Fetching fresh sub-account data...');
      
      // Always fetch fresh data when visiting this page
      dispatch(fetchEnhancedUserInfo())
        .unwrap()
        .then((userInfo) => {
          logger.log('✅ Teams page: User info received:', userInfo);
          logger.log('📊 Account Type:', userInfo.accountType);
          logger.log('👑 Is Master:', userInfo.isMaster);
          logger.log('📁 Master Account Info:', userInfo.masterAccountInfo);
          
          if (userInfo.masterAccountInfo) {
            logger.log('👥 Sub Accounts Count:', userInfo.masterAccountInfo.subAccounts?.length || 0);
            logger.log('👥 Sub Accounts:', userInfo.masterAccountInfo.subAccounts);
          }
        })
        .catch((error) => {
          logger.error('❌ Teams page: Failed to fetch user info:', error);
        });
      
      // Also fetch sub-accounts directly to ensure fresh data
      dispatch(fetchMySubAccounts())
        .unwrap()
        .then((data) => {
          logger.log('✅ Teams page: My sub-accounts received:', data);
        })
        .catch((error) => {
          logger.error('❌ Teams page: Failed to fetch sub-accounts:', error);
        });
    }
  }, [mounted, isAuthenticated, isMasterAccount, dispatch]);

  // Retry handler
  const handleRetry = () => {
    dispatch(fetchEnhancedUserInfo());
    dispatch(fetchMySubAccounts());
  };

  // Handle team card click
  const handleTeamClick = (team: any) => {
    if (team.type === 'sub') {
      navigate(`/teams/details/${team.id}`);
    }
    // Master account card doesn't navigate anywhere (could show master account details if needed)
  };

  // Don't render until mounted (avoid hydration issues)
  if (!mounted) {
    return null;
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    return <LoadingState />;
  }

  // Check if user is a master account
  if (!isMasterAccount) {
    return <AccessDenied />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('subTeamsTitle')}</h1>
              <p className="mt-1 text-sm text-gray-900">
                {t('manageSubAccounts')} • <span className="font-semibold">{allTeamsData?.filter(t => t.type === 'sub').length || 0} {t('subTeamsCount')}</span>
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 hover:shadow-lg transition-all duration-200 shadow-sm cursor-pointer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="font-semibold">{t('createSubAccount')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Loading State */}
        {loading.userInfo || loading.subAccounts ? (
          <LoadingState />
        ) : errors.userInfo || errors.subAccounts ? (
          /* Error State */
          <ErrorState 
            message={errors.userInfo || errors.subAccounts || 'An error occurred'} 
            onRetry={handleRetry}
          />
        ) : !masterAccountInfo ? (
          /* No master account info loaded yet */
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
            <svg className="w-16 h-16 mx-auto text-yellow-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('masterAccountInfoNotLoaded')}</h3>
            <p className="text-gray-900 mb-4">{t('unableToLoadInfo')}</p>
            <ul className="text-left text-sm text-gray-900 max-w-md mx-auto mb-6 space-y-2">
              <li>• {t('apiRequestLoading')}</li>
              <li>• {t('accountMayNotBeMaster')}</li>
              <li>• {t('backendNotReturning')}</li>
            </ul>
            <button
              onClick={handleRetry}
              className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
            >
              {t('retryLoading')}
            </button>
          </div>
        ) : !allTeamsData || allTeamsData.length === 0 ? (
          /* Empty State - No teams in the list */
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('noTeamsData')}</h3>
            <p className="text-gray-900 mb-4">{t('masterAccountLoadedNoTeams')}</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('createSubAccount')}
            </button>
          </div>
        ) : (
          /* Teams List */
          <div>
            {allTeamsData.filter(t => t.type === 'sub').length === 0 ? (
              <EmptyState onCreateClick={() => setShowCreateModal(true)} />
            ) : (
              <div className="space-y-4">
                {/* Only show sub-account teams, exclude master account */}
                {allTeamsData.filter(team => team.type === 'sub').map((team) => (
                  <TeamCard 
                    key={team.id} 
                    team={team} 
                    onClick={() => handleTeamClick(team)} 
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Create Sub-Account Modal */}
      <CreateSubAccountModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          logger.log('✅ Sub-account created successfully - refreshing list');
          // Refresh the sub-accounts list
          dispatch(fetchMySubAccounts());
          dispatch(fetchEnhancedUserInfo());
        }}
      />
    </div>
  );
}
